// EtiquettesDocument.jsx
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import moment from "moment";

const styles = StyleSheet.create({
    page: {
        flexDirection: "column",
        padding: 10,
        width: "150mm",
        height: "100mm",
        fontFamily: 'Helvetica',
    },

    // ============ STRUCTURE =============
    headerRow: {
        flexDirection: "column",
        marginBottom: 10,
    },
    sHeaderRow: {
        flexDirection: "row"
    },
    content: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    footer: {
        marginTop: "auto",
    },

    // ============ PREMIÈRE LIGNE ============
    zoneBlack: {
        backgroundColor: "#000",
        color: "#fff",
        padding: 3,
        width: "60%",
        fontWeight: 700,
        fontSize: 25,
        textAlign: 'center'
    },
    destWhite: {
        backgroundColor: "#fff",
        color: "#000",
        padding: 3,
        width: "40%",
        borderWidth: 1,
        borderColor: "#000",
        fontWeight: 700,
        fontSize: 25,
        textAlign: 'center'
    },

    // ============ TEXTE CENTRAL ============
    textName: {
        textAlign: 'center',
        fontSize: 30,
        fontWeight: 800,
        marginBottom: 5,
    },
    textAddress: {
        textAlign: 'center',
        fontSize: 20,
        fontWeight: 600,
    },

    // ============ DERNIÈRE LIGNE (TABLE) ============
    table: {
        display: 'table',
        width: '100%',
        borderWidth: 1,
        borderColor: '#000',
    },
    tableRow: {
        flexDirection: 'row',
    },
    tableCol1: {
        width: '45%',
        backgroundColor: '#000000',
        color: '#FFFFFF',
        padding: 5,
    },
    tableCol2: {
        width: '45%',
        padding: 5
    },
    tableCol3: {
        width: '10%',
        backgroundColor: '#000000',
        color: '#FFFFFF',
        padding: 5,
        borderLeftWidth: 1,
        borderColor: '#000',
    },
    tableCell: {
        fontSize: 25,
        textAlign: 'center',
        fontWeight: 800,
    },

    // ============ MARQUEURS CAGE ============
    markerTitle: {
        fontSize: 50,
        fontWeight: 800,
        textAlign: 'center',
        marginBottom: 10,
    },
    markerCage: {
        fontSize: 28,
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: 5,
    },
    markerCount: {
        fontSize: 22,
        fontWeight: 600,
        textAlign: 'center',
    },
});

const STATES_LABELS = {
    social: "social",
    locker: "consigne",
    parcel_shop: "commerce",
};

function BagLabel({ bag, index }) {
    const route = bag.routes?.[0] || {};
    const routeName = route.route_name || "";
    const sequenceInRoute = route.sequence_in_route || "";
    const scheduledFor = route.scheduled_for
        ? moment(route.scheduled_for).locale('fr').format('ddd DD MMM YYYY')
        : "";

    const name = bag.name || " ";
    const hub = bag.destination_hub_code || " ";
    const city = bag.city || " ";
    const postalCode = bag.postal_code || " ";
    const pointType = STATES_LABELS[bag.point_to_type] || " ";
    const pointCode = bag.point_to_code || " ";
    const containerLabelCode = bag.container_label_code || " ";
    const bags_to_same_destination = bag.containers_to_same_destination || " ";

    return (
        <Page
            key={index}
            size={{ width: 425.2, height: 283.5 }}
            style={styles.page}
        >
            <View style={styles.headerRow}>
                <View style={styles.sHeaderRow}>
                    <Text style={styles.zoneBlack}>
                        {routeName} #{sequenceInRoute}
                    </Text>
                    <Text style={styles.destWhite}>{hub}</Text>
                </View>
                <View style={styles.sHeaderRow}>
                    <Text style={styles.zoneBlack}>
                        {scheduledFor}
                    </Text>
                    <Text style={styles.destWhite}>{pointCode}</Text>
                </View>
            </View>

            <View style={styles.content}>
                <Text style={styles.textName}>{name.toUpperCase()}</Text>
                <Text style={styles.textAddress}>{city.toUpperCase()} {postalCode.toUpperCase()}</Text>
            </View>

            <View style={styles.footer}>
                <View style={styles.table}>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol1}>
                            <Text style={styles.tableCell}>
                                {pointType.toUpperCase()}
                            </Text>
                        </View>
                        <View style={styles.tableCol2}>
                            <Text style={styles.tableCell}>
                                {containerLabelCode}
                            </Text>
                        </View>
                        <View style={styles.tableCol3}>
                            <Text style={styles.tableCell}>
                                {bags_to_same_destination}
                            </Text>
                        </View>
                    </View>
                </View>
            </View>
        </Page>
    );
}

function MarkerLabel({ cageCode, bagCount, marker }) {
    return (
        <Page
            size={{ width: 425.2, height: 283.5 }}
            style={styles.page}
        >
            <View style={styles.content}>
                <Text style={styles.markerTitle}>{marker}</Text>
                <Text style={styles.markerCage}>{cageCode}</Text>
                <Text style={styles.markerCount}>{bagCount} sac{bagCount > 1 ? 's' : ''}</Text>
            </View>
        </Page>
    );
}

export default function LabelDocument({ bags, type }) {
    if (type === "cage") {
        // Group bags by cage_code preserving order
        const cageGroups = [];
        const cageMap = {};
        for (const bag of bags) {
            const code = bag.cage_code;
            if (!cageMap[code]) {
                cageMap[code] = [];
                cageGroups.push(code);
            }
            cageMap[code].push(bag);
        }

        return (
            <Document>
                {cageGroups.map((cageCode) => {
                    const cageBags = cageMap[cageCode];
                    return [
                        <MarkerLabel key={`start-${cageCode}`} cageCode={cageCode} bagCount={cageBags.length} marker="DEBUT" />,
                        ...cageBags.map((bag, i) => (
                            <BagLabel key={`${cageCode}-${i}`} bag={bag} index={i} />
                        )),
                        <MarkerLabel key={`end-${cageCode}`} cageCode={cageCode} bagCount={cageBags.length} marker="FIN" />,
                    ];
                })}
            </Document>
        );
    }

    return (
        <Document>
            {bags.map((bag, index) => (
                <BagLabel key={index} bag={bag} index={index} />
            ))}
        </Document>
    );
}